import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import {
  useAuthStore,
  selectUser,
  selectIsAuthenticated,
  selectIsAdmin,
} from '../../hooks';
import { useGetUserById } from '../../hooks/use-user';
import {
  LayoutDashboard,
  Users,
  FileWarning,
  MessageSquareWarning,
  ScrollText,
  Shield,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Wallet,
  Loader2,
  Activity,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';

const navItems = [
  {
    title: 'Dashboard',
    href: '/admin',
    icon: LayoutDashboard,
  },
  {
    title: 'Users',
    href: '/admin/users',
    icon: Users,
  },
  {
    title: 'Content',
    href: '/admin/content',
    icon: FileWarning,
  },
  {
    title: 'Communities',
    href: '/admin/communities',
    icon: Shield,
  },
  {
    title: 'Reports',
    href: '/admin/reports',
    icon: MessageSquareWarning,
  },
  {
    title: 'Node Health',
    href: '/admin/node-health',
    icon: Activity,
  },
  {
    title: 'Audit Logs',
    href: '/admin/audit-logs',
    icon: ScrollText,
  },
];

// Security: Allowed admin roles
const ADMIN_ROLES = ['admin', 'moderator'];

// Security: Maximum wait time for wallet connection (prevents indefinite loading)
const MAX_WALLET_WAIT_MS = 10000; // 10 seconds

export default function AdminLayout() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const user = useAuthStore(selectUser);
  const isAdmin = useAuthStore(selectIsAdmin);
  const { walletAddress } = useAuthStore();

  // Only fetch user data when we have a wallet address
  const {
    data: fetchedUser,
    isLoading: isUserLoading,
    isFetched: isUserFetched,
    isSuccess: isUserSuccess,
    isError: isUserError,
  } = useGetUserById(walletAddress, {
    enabled: !!walletAddress, // Explicit: only run when we have address
    retry: 2,
    staleTime: 30000, // 30 seconds
  });

  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [walletTimeout, setWalletTimeout] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  // Security: Set timeout to prevent indefinite loading if wallet never connects
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!walletAddress) {
        setWalletTimeout(true);
      }
    }, MAX_WALLET_WAIT_MS);

    return () => clearTimeout(timer);
  }, [walletAddress]);

  // Security: Check access once user data is loaded
  useEffect(() => {
    // Only check access if we have a wallet and the query has completed
    if (!walletAddress || isUserLoading) {
      return;
    }

    // Query completed (success or error)
    if (isUserSuccess || isUserError) {
      // Use fetchedUser or store user
      const currentUser = fetchedUser || user;
      if (!currentUser || !ADMIN_ROLES.includes(currentUser.role)) {
        // Log unauthorized access attempt in development
        if (import.meta.env.DEV) {
          console.warn('[Security] Unauthorized admin access attempt:', {
            wallet: walletAddress,
            userRole: currentUser?.role,
          });
        }
        setAccessDenied(true);
      } else {
        setAccessDenied(false);
      }
    }
  }, [
    fetchedUser,
    user,
    isUserSuccess,
    isUserError,
    isUserLoading,
    walletAddress,
  ]);

  // Determine loading state
  const isWalletConnecting = !walletAddress && !walletTimeout;
  // Wait for user data when we have a wallet address
  const isWaitingForUser =
    walletAddress && (isUserLoading || (!isUserSuccess && !isUserError));
  const isLoading = isWalletConnecting || isWaitingForUser;

  // Security: Show loading while wallet is connecting (up to timeout)
  if (isLoading && !accessDenied) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
          <p className="text-zinc-400 text-sm">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Security: Wallet connection timed out - show connect prompt (not redirect)
  if (walletTimeout && !walletAddress) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md px-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center">
            <Wallet className="h-8 w-8 text-zinc-500" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white mb-2">
              Wallet Required
            </h1>
            <p className="text-zinc-400 text-sm">
              Please connect your wallet to access this area.
            </p>
          </div>
          <Button
            onClick={() => (window.location.href = '/sign-in')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Connect Wallet
          </Button>
        </div>
      </div>
    );
  }

  // Security: Access denied - show generic error (don't reveal admin exists)
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md px-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
            <ShieldAlert className="h-8 w-8 text-red-500" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white mb-2">
              Access Denied
            </h1>
            <p className="text-zinc-400 text-sm">
              You don't have permission to access this page.
            </p>
          </div>
          <Button
            onClick={() => (window.location.href = '/forum')}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Go to Forum
          </Button>
        </div>
      </div>
    );
  }

  // Final safety check - if we get here without valid admin user, deny access
  if (!user || !ADMIN_ROLES.includes(user.role)) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md px-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
            <ShieldAlert className="h-8 w-8 text-red-500" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white mb-2">
              Access Denied
            </h1>
            <p className="text-zinc-400 text-sm">
              You don't have permission to access this page.
            </p>
          </div>
          <Button
            onClick={() => (window.location.href = '/forum')}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Go to Forum
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex">
      {/* Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/10 blur-[120px]" />
      </div>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden lg:flex flex-col border-r border-zinc-800 bg-zinc-900/50 transition-all duration-300',
          sidebarCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-blue-500" />
              <span className="font-bold text-lg">Admin Portal</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="text-zinc-400 hover:text-white"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href !== '/admin' &&
                location.pathname.startsWith(item.href));

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 text-sm transition-colors',
                  isActive
                    ? 'bg-blue-500/10 text-blue-400 border-r-2 border-blue-500'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.title}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800">
          <Link
            to="/forum"
            className={cn(
              'flex items-center gap-3 px-2 py-2 text-sm text-zinc-400 hover:text-white transition-colors',
              sidebarCollapsed && 'justify-center'
            )}
          >
            <LogOut className="h-5 w-5" />
            {!sidebarCollapsed && <span>Back to Forum</span>}
          </Link>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-zinc-900 border-b border-zinc-800 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-blue-500" />
          <span className="font-bold">Admin</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </Button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-zinc-900 z-40">
          <nav className="p-4">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 text-sm transition-colors rounded-lg mb-1',
                    isActive
                      ? 'bg-blue-500/10 text-blue-400'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.title}</span>
                </Link>
              );
            })}
            <Link
              to="/forum"
              className="flex items-center gap-3 px-4 py-3 text-sm text-zinc-400 hover:text-white mt-4"
            >
              <LogOut className="h-5 w-5" />
              <span>Back to Forum</span>
            </Link>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:pt-0 pt-16 overflow-auto">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
